import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useSelector } from 'react-redux';
import './Catalog.scss';
import HeroCarousel from '../../Components/HeroCarousel';
import SearchField from '../../Components/SearchField/SearchField';
import ActionCard from '../../Components/ActionCard/ActionCard';
import GenresTile from '../../Components/GenresTile/GenresTile';
import SongsTile from '../../Components/SongsTile/SongsTile';
import FAQ from '../../Components/FAQ/FAQ';
import { setSelectedGenre, setSearchQuery, saveToHistory, setIsChangePackageFlow } from '../../FuntoneSlice';
import { useScreenNavigation } from '../../Utils/ScreenNavigationUtils';
import Config from 'app/Config';
import { PopUpEnum } from '../../FuntoneSlice';
import { getCatalog, setPopUp } from 'app/Components/Block/FuntoneBlock/FuntoneSlice';
import { showAppLoader, hideAppLoader } from 'app/Components/Shared/Loader/LoaderSlice';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { dwhFuntoneLobbyPageDisplay, dwhFuntoneGenrePageDisplay } from '../../Reports/dwhReports';
import { getCommonReportParams } from '../../Reports/reportHelpers';
import { saveStateToHistory, loadStateFromHistory } from 'app/utilities/stateUtils';

const Catalog = (props) => {
    const dispatch = useDispatch();
    const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);
    const [loadingStates, setLoadingStates] = useState({});
    const [catalogSongs, setCatalogSongs] = useState({});
    const [expectedCategories, setExpectedCategories] = useState([]);

    const { setPopUp } = props;

    // Get selected subscriber data from state
    const { selectedSubscriber, subscribersData, isChangePackageFlow, popUp } = useSelector((state) => state.FuntoneSlice);

    const contentData = props.contentData || {};
    const {
        heroMobile = { expandedValue: [] },
        heroDesktop = { expandedValue: [] },
        actionCardButtonText = { value: '' },
        actionCardImage = null,
        actionCardSubtitle = { value: '' },
        actionCardTitle = { value: '' },
        faq = { expandedValue: [] },
        genresTitle = { value: '' },
    } = contentData;

    const {
        isFullAuthenticated = false,
        isOtpAuthenticated = false,
        isOtpExtendedAuthenticated = false,
        isSilentOtpAuthenticated = false,
    } = props.loginData || {};

    const { IsContactPerson = false, IsPrimaryContact = false } = (props.loginData && props.loginData.userDetails) || {};
    // Screen navigation hooks must be used at the top-level of the component
    const { navigateToSongs, navigateToSearch, navigateToSelectSubscriber, navigateToAdminPanel } = useScreenNavigation();

    const GENRES_TO_FETCH = contentData?.catalogIds?.value?.map((id) => parseInt(id, 10)); // Convert string array to int array

    const init = async () => {
        try {
            setIsLoadingCatalog(true);
            // Get main catalog (categories)
            const catalog = await props.getCatalog(0);

            if (catalog && catalog.payload && catalog.payload.errorCode === 0) {
                const categories = catalog.payload.results.folders;
                setExpectedCategories(categories);

                // Filter categories based on GENRES_TO_FETCH configuration
                const categoriesToFetch = GENRES_TO_FETCH ? categories.filter((cat) => GENRES_TO_FETCH.includes(cat.id)) : categories;

                // Initialize loading states for categories we're going to fetch
                const initialLoadingStates = {};
                categoriesToFetch.forEach((category) => {
                    initialLoadingStates[category.id] = true;
                });
                setLoadingStates(initialLoadingStates);

                // NOW we can set catalog loading to false since we have the categories
                setIsLoadingCatalog(false);

                // Fetch songs for filtered categories sequentially with delay to avoid overwhelming the server
                for (const category of categoriesToFetch) {
                    try {
                        // Add a small delay between requests to prevent server overload
                        await new Promise((resolve) => setTimeout(resolve, 200));

                        const categoryData = await props.getCatalog(category.id);

                        if (categoryData && categoryData.payload && categoryData.payload.errorCode === 0) {
                            const songs = categoryData.payload.results.items || [];

                            if (songs.length > 0) {
                                setCatalogSongs((prev) => ({
                                    ...prev,
                                    [category.id]: {
                                        title: category.description,
                                        songs: songs.map((song) => ({
                                            id: song.productId || song.id,
                                            description: song.description,
                                            author: song.author,
                                            duration: song.duration,
                                            image: song.image,
                                            isDownloadable: true,
                                            isPlayable: true,
                                            funtonPrevUrl: song.funtonPrevUrl,
                                        })),
                                    },
                                }));
                            }
                        }

                        // Update loading state for this category
                        setLoadingStates((prev) => ({
                            ...prev,
                            [category.id]: false,
                        }));
                    } catch (error) {
                        console.error(`Error fetching songs for category ${category.id}:`, error);
                        // Still update loading state even on error
                        setLoadingStates((prev) => ({
                            ...prev,
                            [category.id]: false,
                        }));
                    }
                }
            }
        } catch (error) {
            console.error('Error initializing catalog:', error);
            setIsLoadingCatalog(false);
        }
    };

    // Save to history only once subscribersData is available so the snapshot
    // always contains subscriber data. Saving before the fetch completes would
    // store subscribersData: null, and restoring that snapshot on browser-back
    // would wipe the data and trigger a duplicate getSubscribersData call.
    useEffect(() => {
        if (subscribersData) {
            dispatch(saveToHistory('catalog'));
            // Patch the just-saved session-storage snapshot to clear any open popup.
            // The actual session storage key is the current URL path (e.g. /funtone::#catalog),
            // not the short label passed to saveToHistory.
            const historyKey = window.location.pathname + '::' + window.location.hash;
            const saved = loadStateFromHistory(historyKey);
            if (saved?.FuntoneSlice) {
                saveStateToHistory(historyKey, {
                    ...saved,
                    FuntoneSlice: { ...saved.FuntoneSlice, popUp: null },
                });
            }
        }
    }, [dispatch, subscribersData]);

    // Clear change package flow flag when entering Catalog
    useEffect(() => {
        if (isChangePackageFlow) {
            dispatch(setIsChangePackageFlow(false));
        }
    }, [dispatch, isChangePackageFlow]);

    useEffect(() => {

        init();

        // Report lobby page display
        const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);
        const isExistingSubscriber = selectedSubscriber?.hasService ? 'מנוי קיים' : 'לא מנוי המתנה נעימה';
        dwhFuntoneLobbyPageDisplay({
            ...reportParams,
            ban: props.loginData?.userDetails?.ban || '',
            crmId: props.loginData?.userDetails?.crmId || '',
            isExistingSubscriber,
        });
    }, []);

    // Transform catalog data to genres format for GenresTile
    const getGenresFromCatalog = () => {
        const { categories } = props.funtoneSlice;
        if (!categories[0] || !categories[0].folders) {
            return [];
        }

        return categories[0].folders.map((folder) => ({
            id: folder.id,
            title: folder.description,
            image: folder.image,
            textColor: '#FFFFFF', // Default color, can be customized
        }));
    };

    // Build slides array from hero data. Each expandedValue item is an object with nested fields.
    const buildSlidesFromHero = (hero) => {
        console.log(hero);
        if (!hero || !Array.isArray(hero.expandedValue)) return [];
        return hero.expandedValue.map((item, index) => {
            // title and subtitle are nested objects with .value
            const title = item.title?.value || '';
            const subtitle = item.subtitle?.value || '';
            const mediaTypeRaw = item.mediaType?.value || '';
            const variant = mediaTypeRaw ? mediaTypeRaw.toString().toLowerCase() : 'image';

            let image = variant == "video" ? Config.epiBaseUrl + item.video?.expandedValue?.url : Config.epiBaseUrl + item.image?.expandedValue?.url;
            // fallback to a provided hero image string if image is missing
            if (!image && item.image && typeof item.image === 'string') image = item.image;

            return {
                id: index,
                image,
                title,
                subtitle,
                variant,
            };
        });
    };

    const slides = isMobile ? buildSlidesFromHero(heroMobile) : buildSlidesFromHero(heroDesktop);

    // Monitor authentication changes to navigate after login
    useEffect(() => {
        const isAuthenticated = isFullAuthenticated || isOtpAuthenticated || isOtpExtendedAuthenticated || isSilentOtpAuthenticated;

        // Check if we should navigate to admin panel after login
        // This happens when user becomes authenticated and we have the action card rendered
        if (isAuthenticated && subscribersData) {
            // Check if we just came from login popup (popup is closed but we're authenticated)
            // Note: This is a simple check - you might want to add a more robust state flag
            const shouldNavigateToAdmin = sessionStorage.getItem('navigateToAdminAfterLogin');
            if (shouldNavigateToAdmin === 'true') {
                sessionStorage.removeItem('navigateToAdminAfterLogin');
                navigateToAdminPanel();
            }
        }
    }, [isFullAuthenticated, isOtpAuthenticated, isOtpExtendedAuthenticated, isSilentOtpAuthenticated, subscribersData, navigateToAdminPanel]);

    const handleActionCardClick = (cardType) => {
        if (!isFullAuthenticated && !isOtpAuthenticated && !isOtpExtendedAuthenticated && !isSilentOtpAuthenticated) {
            // Set flag to navigate after successful login
            sessionStorage.setItem('navigateToAdminAfterLogin', 'true');
            setPopUp({ type: PopUpEnum.LOGIN, stage: 'PHONE' });
            return;
        }

        // Navigate directly - user is already authenticated
        navigateToAdminPanel();
    };

    const handleGenreClick = (genre) => {
        const genreData = expectedCategories.find((cat) => cat.id == genre.id);

        if (genreData) {
            const categoryData = catalogSongs[genre.id];

            // Navigate immediately — SongsPage will show skeletons while fetching if songs aren't ready yet
            props.setSelectedGenre({
                genre: {
                    id: genre.id,
                    title: genre.title,
                    description: genre.title,
                    image: genreData.image,
                },
                songs: categoryData?.songs || [],
            });

            // Report genre page display
            const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);
            const isExistingSubscriber = selectedSubscriber?.hasService ? 'מנוי קיים' : 'לא מנוי המתנה נעימה';
            dwhFuntoneGenrePageDisplay({
                ...reportParams,
                ban: props.loginData?.userDetails?.ban || '',
                crmId: props.loginData?.userDetails?.crmId || '',
                isExistingSubscriber,
                genreName: genre.title || '',
            });

            navigateToSongs({ params: { id: genre.id } });
        }
    };


    const handleViewAllSongs = (categoryId, categoryTitle) => {
        // Find the genre data for this category
        const genre = expectedCategories.find((cat) => cat.id == categoryId);
        const categoryData = catalogSongs[categoryId];

        if (genre) {
            // Set the selected genre with available data or empty array
            props.setSelectedGenre({
                genre: {
                    id: categoryId,
                    title: categoryTitle,
                    description: categoryTitle,
                    image: genre.image, // Include the image from the category API data
                },
                songs: categoryData?.songs || [], // Use already fetched songs if available
            });
            navigateToSongs({ params: { id: categoryId } });
            // SongsPage will check Redux categories for cached data before making API calls
        }
    };

    const handleViewAllGenres = () => {
        // For genres "view all", we can show all available genres
        // Since we don't have a dedicated genres page, let's show the first genre's songs
        const genres = getGenresFromCatalog();
        if (genres.length > 0) {
            const firstGenre = genres[0];
            props.setSelectedGenre({
                genre: firstGenre,
                songs: [],
            });
            navigateToSongs();
            // This will trigger getCatalog in SongsPage component with firstGenre.id
        }
    };

    const handleSearch = (query) => {
        // Set the search query in Redux before navigating
        props.setSearchQuery(query);
        navigateToSearch();
    };

    const handleSearchChange = (value) => {
        // When user starts typing, set the query and navigate to search page
        if (value && value.length > 0) {
            props.setSearchQuery(value);
        }
    };

    // Sample FAQ data
    const faqData = faq.expandedValue?.map((item) => ({
        question: item.question?.value || '',
        answer: item.answer?.value || '',
    }));

    const genres = getGenresFromCatalog();

    // Check if any subscriber has service
    const anySubscriberHasService = useMemo(() => {
        if (!subscribersData || subscribersData.length === 0) return false;
        return subscribersData.some(subscriber => subscriber.hasService);
    }, [subscribersData]);

    return (
        <div className='catalog-page'>
            <div className={'top-container'}>
                <HeroCarousel slides={slides} autoPlay={false} />
            </div>
            <div className={'content-container'}>
                <SearchField
                    placeholder='לאיזה שיר אנשים יקשיבו...'
                    onSearch={handleSearch}
                    onChange={handleSearchChange}
                    onFocus={() => navigateToSearch()}
                />

                {anySubscriberHasService &&
                    (isFullAuthenticated || isOtpAuthenticated || isOtpExtendedAuthenticated || isSilentOtpAuthenticated) && (
                        <ActionCard
                            title={actionCardTitle.value}
                            buttonText={actionCardButtonText.value}
                            buttonVariant='primary'
                            image={Config.epiBaseUrl + actionCardImage?.expandedValue?.url}
                            imageAlt='Funtone music'
                            onButtonClick={() => handleActionCardClick('custom-ringtones')}
                            variant='featured'
                        />
                    )}

                <GenresTile
                    title={genresTitle.value}
                    genres={genres}
                    onCategoryClick={handleGenreClick}
                    isLoading={isLoadingCatalog}
                    onViewAllClick={handleViewAllGenres}
                />

                <div className='songs-tiles-container'>
                    {expectedCategories.map((category) => {
                        const isLoading = loadingStates[category.id];
                        const categoryData = catalogSongs[category.id];

                        if (isLoading || (categoryData && categoryData.songs.length > 0)) {
                            return (
                                <SongsTile
                                    key={category.id}
                                    songs={categoryData?.songs || []}
                                    title={category.description}
                                    isLoading={isLoading}
                                    onViewAllClick={() => handleViewAllSongs(category.id, category.description)}
                                    maxColumns={!isMobile && 3}
                                    skeletonWidth={308}
                                    skeletonHeight={isMobile ? 64 : 84}
                                />
                            );
                        }

                        return null;
                    })}
                </div>
                {!(isFullAuthenticated || isOtpAuthenticated || isOtpExtendedAuthenticated || isSilentOtpAuthenticated) && (
                    <ActionCard
                        title={actionCardTitle.value}
                        buttonText={actionCardButtonText.value}
                        buttonVariant='primary'
                        image={Config.epiBaseUrl + actionCardImage?.expandedValue?.url}
                        imageAlt='Funtone music'
                        onButtonClick={() => handleActionCardClick('custom-ringtones')}
                        variant='featured'
                    />
                )}
                {faqData.length > 0 && <FAQ items={faqData} />}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        funtoneSlice: state.FuntoneSlice,
        loginData: state.authentication.loginData,
    };
};

const mapDispatchToProps = (dispatch) => ({
    dispatch,
    setPopUp: (data) => dispatch(setPopUp(data)),
    getCatalog: (data) => dispatch(getCatalog(data)),
    setSelectedGenre: (data) => dispatch(setSelectedGenre(data)),
    setSearchQuery: (data) => dispatch(setSearchQuery(data)),
    setIsChangePackageFlow: (data) => dispatch(setIsChangePackageFlow(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Catalog);
